import type {
	AcceptedAudioFormat,
	RecordingPolicyPayload,
} from '@Application/RecordingPolicy/Actions/recordingPolicyFetched';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor. Every limit it
 * exposes comes straight from the API payload — nothing here is a front-coded fallback, so a change
 * of policy on the server is reflected without a front deployment.
 */
class RecordingPolicyView {
	readonly segmentMaxBytes: number;
	readonly maxSegments: number;
	readonly maxDurationMs: number;
	readonly deviceRetentionMs: number;
	readonly acceptedAudioFormats: readonly AcceptedAudioFormat[];

	constructor(payload: RecordingPolicyPayload) {
		this.segmentMaxBytes = payload.segmentMaxBytes;
		this.maxSegments = payload.maxSegments;
		this.maxDurationMs = payload.maxDurationMs;
		this.deviceRetentionMs = payload.deviceRetentionMs;
		this.acceptedAudioFormats = payload.acceptedAudioFormats;

		Object.freeze(this);
	}

	/** Extensions in policy order, deduplicated (several MIME types can share an extension). */
	get acceptedExtensions(): readonly string[] {
		const seenExtensions = new Set<string>();
		const orderedExtensions: string[] = [];

		for (const format of this.acceptedAudioFormats) {
			if (!seenExtensions.has(format.extension)) {
				seenExtensions.add(format.extension);
				orderedExtensions.push(format.extension);
			}
		}

		return orderedExtensions;
	}

	/** e.g. "webm, m4a, mp3, wav" — extensions without their leading dot, for an inline sentence. */
	get acceptedFormatsSentence(): string {
		return this.acceptedExtensions.map((extension) => extension.replace(/^\./, '')).join(', ');
	}

	/**
	 * e.g. "1 h 30" or "45 min" — never hard-coded, always derived from `maxDurationMs`. Minutes are
	 * zero-padded to two digits once there is at least one hour (`3 h 00`, `1 h 05`), matching
	 * `formatDuration` (`UI/Helpers/OutboxRunner/formatDuration.ts`): two neighbouring helpers rendering a
	 * duration must not disagree on its shape.
	 */
	get maxDurationSentence(): string {
		const totalMinutes = Math.round(this.maxDurationMs / 60_000);
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		if (hours === 0) {
			return `${minutes} min`;
		}

		return `${hours} h ${minutes.toString().padStart(2, '0')}`;
	}

	/** Case-insensitive lookup; `extension` is expected with its leading dot (e.g. `.mp3`). */
	findFormatByExtension(extension: string): AcceptedAudioFormat | null {
		const normalizedExtension = extension.toLowerCase();

		const found = this.acceptedAudioFormats.find(
			(format) => format.extension.toLowerCase() === normalizedExtension,
		);

		return found ?? null;
	}
}

export default RecordingPolicyView;
